import { autoinject, bindable } from 'aurelia-framework';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { ComponentBase, SortDirection, Sorting, IForm } from 'app/shared/lib/essette-site';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { IBaseConfig } from 'app/core/models/essette-interfaces';
import { IPopup } from 'app/shared/elements/popup-base';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { I18N } from 'aurelia-i18n';
import { MemberService } from 'app/cd/services/member-service';
import { LookupService } from 'app/core/services/lookup-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { SaveGender } from 'app/core/components/genders/save-gender';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import * as Core from 'app/core/models/essette-api';

export enum FlagOptions {
    availableAsSex = 'cd:member.gender.availableAsSex.$',
    availableAsIdentity = 'cd:member.gender.availableAsIdentity.$'
}

export enum IdentityOptions {
    male = 'cd:member.gender.male.$',
    female = 'cd:member.gender.female.$'
}

interface CustomGender extends Core.Gender {
    flagCount?: number | null;
    flagToolTipDesc?: string | null;
}
@autoinject()
export class Genders extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable
    protected config: IBaseConfig = {};

    protected genders: ReadonlyArray<CustomGender> | null = null;

    private sorting = new Sorting();
    protected resultCount = 0;
    protected page = 0;
    protected pageSize = 25;
    protected includeInactive = false;

    protected validate = validate;

    protected setGenderFlags = (genders: CustomGender[]): CustomGender[] => {
        genders.forEach(gender => {
            let listDisplay = '<ul>';
            gender.flagCount = 0;
            for(const [flag, value] of Object.entries({ ...gender.flag })) {
                if(value) {
                    gender.flagCount++;
                    listDisplay += '<li>' + this.i18n.tr('cd:member.gender.' + flag + '.$') + '</li>';
                }
            }
            listDisplay += '</ul>';
            gender.flagToolTipDesc = listDisplay;
        });
        return genders;
    }

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $saveGenderModal!: IPopup;
    protected $form!: IForm;
    protected $genderForm!: Element;
    protected $saveGender!: SaveGender;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly lookupService: LookupService,
        protected readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.reload();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveGenderModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadGenders();
    }

    protected saveGender(gender: Core.Gender): void {
        this.$saveGender.gender = gender;
        this.$saveGenderModal.open();
    }

    protected saveGenderClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveGenderModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadGenders(): Promise<void> {
        const includes: string[] = [];

        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            top: this.pageSize,
            includeInactive: this.includeInactive
        };

        try {
            this.state.beginLoad();

            const response = await this.lookupService.loadGenders(options, includes);
            this.genders = this.setGenderFlags(response.results);
            this.genders = response.results;
            this.resultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateGender(Gender: Core.Gender): Promise<void> {
        if(!Gender.id) return;

        // Show confirmation dialog only if deactivating
        if(Gender.isActive) {
            const result = await confirmation.showDeactivate('cd:member.gender.$', 1, false);
            if(result.dialogResponse !== confirmation.options.OK) { return; }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.activateGender(Gender.id, !Gender.isActive);
            isSuccessful = response.isSuccessful;
            await this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:member.gender.$', !Gender.isActive, isSuccessful);
            this.state.endSave();
        }
    }
}
